import React, { useEffect } from "react";

import {
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  Tooltip,
} from "@mui/material";

import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";

import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";

import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";

import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";

import ImageIcon from "@mui/icons-material/Image";
import TableChartIcon from "@mui/icons-material/TableChart";

import FunctionsIcon from "@mui/icons-material/Functions";
import SuperscriptIcon from "@mui/icons-material/VerticalAlignTop";
import SubscriptIcon from "@mui/icons-material/VerticalAlignBottom";

import { useEditor, EditorContent } from "@tiptap/react";

import StarterKit from "@tiptap/starter-kit";

import Underline from "@tiptap/extension-underline";

import Image from "@tiptap/extension-image";

import TextAlign from "@tiptap/extension-text-align";

import Placeholder from "@tiptap/extension-placeholder";

import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";

import Superscript from "@tiptap/extension-superscript";
import Subscript from "@tiptap/extension-subscript";

import Mathematics from "@tiptap/extension-mathematics";

import "katex/dist/katex.min.css";


/* =========================================================
   Math Symbols
========================================================= */

const MATH_SYMBOLS = [
  {
    label: "±",
    value: "±",
  },
  {
    label: "×",
    value: "×",
  },
  {
    label: "÷",
    value: "÷",
  },
  {
    label: "√",
    value: "√",
  },
  {
    label: "∞",
    value: "∞",
  },
  {
    label: "π",
    value: "π",
  },
  {
    label: "θ",
    value: "θ",
  },
  {
    label: "α",
    value: "α",
  },
  {
    label: "β",
    value: "β",
  },
  {
    label: "γ",
    value: "γ",
  },
  {
    label: "Δ",
    value: "Δ",
  },
  {
    label: "Σ",
    value: "Σ",
  },
  {
    label: "∑",
    value: "∑",
  },
  {
    label: "∫",
    value: "∫",
  },
  {
    label: "≤",
    value: "≤",
  },
  {
    label: "≥",
    value: "≥",
  },
  {
    label: "≠",
    value: "≠",
  },
  {
    label: "≈",
    value: "≈",
  },
  {
    label: "∴",
    value: "∴",
  },
  {
    label: "∵",
    value: "∵",
  },
  {
    label: "°",
    value: "°",
  },
];


/* =========================================================
   Component
========================================================= */

const TiptapEditor = ({
  content,
  onChange,
}) => {

  /* =======================================================
     Editor
  ======================================================= */

  const editor = useEditor({

    extensions: [

      StarterKit,

      Underline,

      Superscript,

      Subscript,

      Image.configure({
        inline: false,
        allowBase64: true,
      }),

      TextAlign.configure({
        types: [
          "heading",
          "paragraph",
        ],
      }),

      Placeholder.configure({
        placeholder:
          "Type your question here...",
      }),

      Table.configure({
        resizable: true,
      }),

      TableRow,

      TableHeader,

      TableCell,

      Mathematics.configure({
        katexOptions: {
          throwOnError: false,
        },
      }),
    ],

    content: content || {
      type: "doc",
      content: [
        {
          type: "paragraph",
        },
      ],
    },

    onUpdate: ({ editor }) => {

      if (onChange) {
        onChange(
          editor.getJSON()
        );
      }
    },
  });


  /* =======================================================
     Update Content From Parent
  ======================================================= */

  useEffect(() => {

    if (!editor) {
      return;
    }

    const currentContent =
      editor.getJSON();

    const incomingContent =
      content || {
        type: "doc",
        content: [
          {
            type: "paragraph",
          },
        ],
      };

    if (
      JSON.stringify(
        currentContent
      ) !==
      JSON.stringify(
        incomingContent
      )
    ) {

      editor.commands.setContent(
        incomingContent,
        false
      );
    }

  }, [content, editor]);


  /* =======================================================
     Loading
  ======================================================= */

  if (!editor) {
    return null;
  }


  /* =======================================================
     Insert Math Equation
  ======================================================= */

  const insertEquation = () => {

    const latex = window.prompt(
      "Enter mathematical formula in LaTeX",
      "x^2 + y^2 = z^2"
    );

    if (
      latex === null ||
      latex.trim() === ""
    ) {
      return;
    }

    editor
      .chain()
      .focus()
      .insertContent({
        type: "inlineMath",
        attrs: {
          latex: latex.trim(),
        },
      })
      .run();
  };


  /* =======================================================
     Insert Block Equation
  ======================================================= */

  const insertBlockEquation = () => {

    const latex = window.prompt(
      "Enter equation in LaTeX",
      "\\frac{a+b}{c}"
    );

    if (
      latex === null ||
      latex.trim() === ""
    ) {
      return;
    }

    editor
      .chain()
      .focus()
      .insertContent({
        type: "blockMath",
        attrs: {
          latex: latex.trim(),
        },
      })
      .run();
  };


  /* =======================================================
     Insert Symbol
  ======================================================= */

  const insertSymbol = (
    symbol
  ) => {

    editor
      .chain()
      .focus()
      .insertContent(symbol)
      .run();
  };


  /* =======================================================
     Insert Image
  ======================================================= */

  const insertImage = () => {

    const url = window.prompt(
      "Enter image URL"
    );

    if (
      !url ||
      url.trim() === ""
    ) {
      return;
    }

    editor
      .chain()
      .focus()
      .setImage({
        src: url.trim(),
      })
      .run();
  };


  /* =======================================================
     Insert Table
  ======================================================= */

  const insertTable = () => {

    editor
      .chain()
      .focus()
      .insertTable({
        rows: 3,
        cols: 3,
        withHeaderRow: true,
      })
      .run();
  };


  /* =======================================================
     Toolbar Button
  ======================================================= */

  const ToolButton = ({
    title,
    onClick,
    active = false,
    children,
  }) => {

    return (
      <Tooltip title={title}>

        <IconButton
          size="small"
          onClick={onClick}
          sx={{
            borderRadius: 1,

            backgroundColor:
              active
                ? "action.selected"
                : "transparent",
          }}
        >
          {children}
        </IconButton>

      </Tooltip>
    );
  };


  /* =======================================================
     Render
  ======================================================= */

  return (

    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden",
        backgroundColor: "#fff",
      }}
    >

      {/* ===================================================
          TOOLBAR
      =================================================== */}

      <Box
        sx={{
          p: 1,

          backgroundColor:
            "grey.50",

          borderBottom:
            "1px solid",

          borderColor:
            "divider",

          overflowX: "auto",
        }}
      >

        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          flexWrap="wrap"
        >

          {/* -----------------------------------------------
              Bold
          ------------------------------------------------ */}

          <ToolButton
            title="Bold"
            active={editor.isActive(
              "bold"
            )}
            onClick={() =>
              editor
                .chain()
                .focus()
                .toggleBold()
                .run()
            }
          >
            <FormatBoldIcon fontSize="small" />
          </ToolButton>


          {/* -----------------------------------------------
              Italic
          ------------------------------------------------ */}

          <ToolButton
            title="Italic"
            active={editor.isActive(
              "italic"
            )}
            onClick={() =>
              editor
                .chain()
                .focus()
                .toggleItalic()
                .run()
            }
          >
            <FormatItalicIcon fontSize="small" />
          </ToolButton>


          {/* -----------------------------------------------
              Underline
          ------------------------------------------------ */}

          <ToolButton
            title="Underline"
            active={editor.isActive(
              "underline"
            )}
            onClick={() =>
              editor
                .chain()
                .focus()
                .toggleUnderline()
                .run()
            }
          >
            <FormatUnderlinedIcon fontSize="small" />
          </ToolButton>


          <Divider
            orientation="vertical"
            flexItem
          />


          {/* -----------------------------------------------
              Superscript
          ------------------------------------------------ */}

          <ToolButton
            title="Superscript"
            active={editor.isActive(
              "superscript"
            )}
            onClick={() =>
              editor
                .chain()
                .focus()
                .toggleSuperscript()
                .run()
            }
          >
            <SuperscriptIcon fontSize="small" />
          </ToolButton>


          {/* -----------------------------------------------
              Subscript
          ------------------------------------------------ */}

          <ToolButton
            title="Subscript"
            active={editor.isActive(
              "subscript"
            )}
            onClick={() =>
              editor
                .chain()
                .focus()
                .toggleSubscript()
                .run()
            }
          >
            <SubscriptIcon fontSize="small" />
          </ToolButton>


          <Divider
            orientation="vertical"
            flexItem
          />


          {/* -----------------------------------------------
              Bullet List
          ------------------------------------------------ */}

          <ToolButton
            title="Bullet List"
            onClick={() =>
              editor
                .chain()
                .focus()
                .toggleBulletList()
                .run()
            }
          >
            <FormatListBulletedIcon fontSize="small" />
          </ToolButton>


          {/* -----------------------------------------------
              Number List
          ------------------------------------------------ */}

          <ToolButton
            title="Numbered List"
            onClick={() =>
              editor
                .chain()
                .focus()
                .toggleOrderedList()
                .run()
            }
          >
            <FormatListNumberedIcon fontSize="small" />
          </ToolButton>


          <Divider
            orientation="vertical"
            flexItem
          />


          {/* -----------------------------------------------
              Alignment
          ------------------------------------------------ */}

          <ToolButton
            title="Align Left"
            onClick={() =>
              editor
                .chain()
                .focus()
                .setTextAlign("left")
                .run()
            }
          >
            <FormatAlignLeftIcon fontSize="small" />
          </ToolButton>


          <ToolButton
            title="Align Center"
            onClick={() =>
              editor
                .chain()
                .focus()
                .setTextAlign("center")
                .run()
            }
          >
            <FormatAlignCenterIcon fontSize="small" />
          </ToolButton>


          <ToolButton
            title="Align Right"
            onClick={() =>
              editor
                .chain()
                .focus()
                .setTextAlign("right")
                .run()
            }
          >
            <FormatAlignRightIcon fontSize="small" />
          </ToolButton>


          <Divider
            orientation="vertical"
            flexItem
          />


          {/* =================================================
              MATH
          ================================================= */}


          {/* -----------------------------------------------
              Inline Equation
          ------------------------------------------------ */}

          <Tooltip title="Insert Math Equation">

            <Button
              size="small"
              variant="outlined"
              startIcon={
                <FunctionsIcon />
              }
              onClick={
                insertEquation
              }
              sx={{
                minWidth: "auto",
              }}
            >
              Math
            </Button>

          </Tooltip>


          {/* -----------------------------------------------
              Block Equation
          ------------------------------------------------ */}

          <Tooltip title="Insert Block Equation">

            <Button
              size="small"
              variant="outlined"
              startIcon={
                <FunctionsIcon />
              }
              onClick={
                insertBlockEquation
              }
              sx={{
                minWidth: "auto",
              }}
            >
              Equation
            </Button>

          </Tooltip>


          <Divider
            orientation="vertical"
            flexItem
          />


          {/* =================================================
              COMMON SYMBOLS
          ================================================= */}

          {MATH_SYMBOLS.map(
            (symbol) => (

              <Tooltip
                key={symbol.value}
                title={`Insert ${symbol.value}`}
              >

                <Button
                  size="small"
                  onClick={() =>
                    insertSymbol(
                      symbol.value
                    )
                  }
                  sx={{
                    minWidth: 34,
                    height: 32,
                    fontSize: 17,
                    fontFamily:
                      "Arial, sans-serif",
                  }}
                >
                  {symbol.label}
                </Button>

              </Tooltip>

            )
          )}


          <Divider
            orientation="vertical"
            flexItem
          />


          {/* =================================================
              IMAGE
          ================================================= */}

          <ToolButton
            title="Insert Image"
            onClick={
              insertImage
            }
          >
            <ImageIcon fontSize="small" />
          </ToolButton>


          {/* =================================================
              TABLE
          ================================================= */}

          <ToolButton
            title="Insert Table"
            onClick={
              insertTable
            }
          >
            <TableChartIcon fontSize="small" />
          </ToolButton>


          <Divider
            orientation="vertical"
            flexItem
          />


          {/* =================================================
              UNDO
          ================================================= */}

          <ToolButton
            title="Undo"
            onClick={() =>
              editor
                .chain()
                .focus()
                .undo()
                .run()
            }
          >
            <UndoIcon fontSize="small" />
          </ToolButton>


          {/* =================================================
              REDO
          ================================================= */}

          <ToolButton
            title="Redo"
            onClick={() =>
              editor
                .chain()
                .focus()
                .redo()
                .run()
            }
          >
            <RedoIcon fontSize="small" />
          </ToolButton>

        </Stack>

      </Box>


      {/* ===================================================
          EDITOR
      =================================================== */}

      <Box
        sx={{
          p: 2,

          minHeight: 220,

          "& .tiptap": {
            outline: "none",

            minHeight: 180,

            fontSize: "16px",

            lineHeight: 1.7,

            "& p": {
              margin: "0 0 8px 0",
            },

            "& h1": {
              fontSize: "28px",
              margin: "12px 0",
            },

            "& h2": {
              fontSize: "24px",
              margin: "10px 0",
            },

            "& h3": {
              fontSize: "20px",
              margin: "8px 0",
            },

            "& ul, & ol": {
              paddingLeft: "28px",
            },

            "& img": {
              maxWidth: "100%",
              height: "auto",
            },

            "& table": {
              width: "100%",
              borderCollapse:
                "collapse",
            },

            "& th, & td": {
              border:
                "1px solid #999",
              padding: "6px",
            },

            /* ---------------------------------------------
               KaTeX
            --------------------------------------------- */

            "& .katex": {
              fontSize:
                "1.15em",
            },

            "& .tiptap-mathematics-render": {
              padding:
                "2px 4px",
              borderRadius:
                "4px",
            },

            "& .tiptap-mathematics-render:hover": {
              backgroundColor:
                "rgba(25, 118, 210, 0.08)",
            },
          },
        }}
      >

        <EditorContent
          editor={editor}
        />

      </Box>

    </Box>
  );
};

export default TiptapEditor;